import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { PageHeader } from 'client/js/util/layout_utils';

const DEFAULT_BASE_URL = '/usercp/filemanager/api';

const ImageLightbox = ({ file, onClose, baseApiUrl = DEFAULT_BASE_URL }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  // Helper function to encode path for URL
  const encodePath = (path) => {
    return btoa(path).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  useEffect(() => {
    const loadImage = async () => {
      try {
        setIsLoading(true);
        const encodedPath = encodePath(file.path);
        const url = `${baseApiUrl}/download?path=${encodeURIComponent(file.path)}`;
        setImageUrl(url);
        setIsLoading(false);
      } catch (err) {
        setError(err.message || 'Fehler beim Laden des Bildes');
        setIsLoading(false);
      }
    };

    loadImage();
  }, [file.path, baseApiUrl]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.25));
  };

  const handleResetZoom = () => {
    setZoom(1);
    setRotation(0);
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleDownload = () => {
    window.location.href = imageUrl;
  };

  const headerButtons = (
    <div className="d-flex align-items-center">
      <div className="btn-group mr-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={handleZoomOut}
          title="Verkleinern"
          disabled={zoom <= 0.25}
        >
          <i className="fa fa-search-minus" />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={handleResetZoom}
          title="Zurücksetzen"
        >
          <i className="fa fa-undo" /> {Math.round(zoom * 100)}%
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={handleZoomIn}
          title="Vergrößern"
          disabled={zoom >= 3}
        >
          <i className="fa fa-search-plus" />
        </button>
      </div>

      <button
        type="button"
        className="btn btn-sm btn-outline-secondary mr-2"
        onClick={handleRotate}
        title="Drehen"
      >
        <i className="fa fa-repeat" />
      </button>

      <button
        type="button"
        className="btn btn-sm btn-primary"
        onClick={handleDownload}
        title="Herunterladen"
      >
        <i className="fa fa-download" />
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <>
        <PageHeader text="Lade Bild..." back_fn={onClose} buttons={headerButtons} />
        <div className="text-center p-5">
          <div className="spinner-wrap"><div className="css-spinloader"></div></div>
          <p className="mt-3">Lade Bild...</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader text="Fehler" back_fn={onClose} buttons={headerButtons} />
        <div className="alert alert-danger m-3">
          <h5>Fehler beim Laden des Bildes</h5>
          <p>{error}</p>
        </div>
      </>
    );
  }

  return (
    <div
      className="image-lightbox-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f8f9fa'
      }}
    >
      <PageHeader
        text={file.name}
        title={file.name}
        buttons={headerButtons}
        back_fn={onClose}
      />

      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'auto',
          padding: '20px',
          backgroundColor: '#2d2d2d'
        }}
        onClick={(e) => {
          // Close on background click, but not on image click
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <img
          src={imageUrl}
          alt={file.name}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            transform: `scale(${zoom}) rotate(${rotation}deg)`,
            transition: 'transform 0.2s ease-in-out',
            cursor: zoom > 1 ? 'move' : 'default'
          }}
          onError={() => setError('Bild konnte nicht geladen werden')}
        />
      </div>

      <div
        style={{
          padding: '10px 20px',
          backgroundColor: '#343a40',
          color: 'white',
          textAlign: 'center',
          fontSize: '14px'
        }}
      >
        <strong>{file.name}</strong>
        {file.size && (
          <span className="ml-3 text-muted">
            {(file.size / 1024).toFixed(2)} KB
          </span>
        )}
      </div>
    </div>
  );
};

ImageLightbox.propTypes = {
  file: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  baseApiUrl: PropTypes.string,
};

export default ImageLightbox;
